import React, { useEffect, useState, useRef } from 'react';
import { X, ChevronDown, Info, Check, MapPin, Phone, Shield, Calendar, Send, Search, Loader2 } from 'lucide-react';
interface ActionPanelProps {
  isOpen: boolean;
  siteName: string;
  mode?: 'site-prefilled' | 'site-searchable';
  onClose: () => void;
  onSubmit?: (data: ActionFormData) => void;
}
export interface ActionFormData {
  site: string;
  actionType: string;
  assignTo: string;
}
interface SuggestedGuard {
  id: string;
  name: string;
  currentShifts: number;
  maxShifts: number;
  grade: string;
  distance: string;
  contact: string;
}
const actionTypes = ['Real-Time Exception', 'Roster Optimisation'];
const assignees = ['John Smith', 'Sarah Johnson', 'Michael Brown', 'Emily Davis', 'David Wilson'];
const sampleSites = ['Woolworths HQ', 'Standard Bank Tower', 'Pick n Pay Constantia', 'Nedbank Plaza', 'Checkers Claremont', 'FNB Building', 'Spar Distribution Centre', 'Dischem Warehouse', 'Shoprite Bellville', 'ABSA Centre', 'Discovery HQ', 'Old Mutual Tower'];
const suggestedGuards: SuggestedGuard[] = [{
  id: 'guard-1',
  name: 'John Mkhize',
  currentShifts: 14,
  maxShifts: 19,
  grade: 'Grade B',
  distance: 'Within 5km',
  contact: '082 345 6789'
}, {
  id: 'guard-2',
  name: 'Thabo Ndlovu',
  currentShifts: 12,
  maxShifts: 19,
  grade: 'Grade B',
  distance: 'Within 3km',
  contact: '083 456 7890'
}, {
  id: 'guard-3',
  name: 'Sipho Dlamini',
  currentShifts: 15,
  maxShifts: 19,
  grade: 'Grade A',
  distance: 'Within 7km',
  contact: '084 567 8901'
}, {
  id: 'guard-4',
  name: 'Nomsa Khumalo',
  currentShifts: 11,
  maxShifts: 19,
  grade: 'Grade B',
  distance: 'Within 4km',
  contact: '085 678 9012'
}, {
  id: 'guard-5',
  name: 'Bongani Zulu',
  currentShifts: 16,
  maxShifts: 19,
  grade: 'Grade C',
  distance: 'Within 6km',
  contact: '086 789 0123'
}];
export function ActionPanel({
  isOpen,
  siteName,
  mode = 'site-prefilled',
  onClose,
  onSubmit
}: ActionPanelProps) {
  const [formData, setFormData] = useState<ActionFormData>({
    site: siteName,
    actionType: '',
    assignTo: ''
  });
  const [selectedGuardId, setSelectedGuardId] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Searchable dropdown state
  const [isSiteDropdownOpen, setIsSiteDropdownOpen] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Update site name when prop changes, but only if not in searchable mode or if we want to reset
  useEffect(() => {
    if (mode === 'site-prefilled') {
      setFormData(prev => ({
        ...prev,
        site: siteName
      }));
    } else if (!isOpen) {
      // Reset when closed
      setFormData(prev => ({
        ...prev,
        site: ''
      }));
      setSiteSearch('');
    }
  }, [siteName, mode, isOpen]);
  // Reset form when panel closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        site: mode === 'site-prefilled' ? siteName : '',
        actionType: '',
        assignTo: ''
      });
      setSelectedGuardId(null);
      setSiteSearch('');
      setIsSiteDropdownOpen(false);
      setIsSubmitting(false);
    }
  }, [isOpen, siteName, mode]);
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, isSubmitting]);
  // Click outside listener for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsSiteDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 800));
    onSubmit?.(formData);
    setIsSubmitting(false);
  };
  const handleChange = (field: keyof ActionFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Reset selected guard when action type changes
    if (field === 'actionType') {
      setSelectedGuardId(null);
    }
  };
  const handleSelectGuard = (guardId: string) => {
    setSelectedGuardId(guardId === selectedGuardId ? null : guardId);
  };
  const handleDispatch = () => {
    if (selectedGuardId) {
      const guard = suggestedGuards.find(g => g.id === selectedGuardId);
      console.log('Dispatching guard:', guard);
      // Here you would typically send this to an API
      onClose();
    }
  };
  const filteredSites = sampleSites.filter(site => site.toLowerCase().includes(siteSearch.toLowerCase()));
  const handleSiteSelect = (site: string) => {
    setFormData(prev => ({
      ...prev,
      site
    }));
    setSiteSearch(site);
    setIsSiteDropdownOpen(false);
  };
  const isRealTimeException = formData.actionType === 'Real-Time Exception';
  return <>
      {/* Backdrop */}
      <div className={`fixed inset-0 bg-[#062133]/40 backdrop-blur-sm z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={!isSubmitting ? onClose : undefined} aria-hidden="true" data-id="element-193" />

      {/* Panel */}
      <div className={`fixed inset-y-0 right-0 z-50 w-full md:w-[480px] bg-white shadow-2xl transform transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} role="dialog" aria-modal="true" aria-labelledby="panel-title" data-id="element-194">
        <div className="flex flex-col h-full" data-id="element-195">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#e5e5e5]" data-id="element-196">
            <h2 id="panel-title" className="text-xl font-bold text-[#062133]" data-id="element-197">
              New Action
            </h2>
            <button onClick={onClose} disabled={isSubmitting} className="p-2 -mr-2 text-[#5a7280] hover:text-[#062133] hover:bg-[#fafaf9] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Close panel" data-id="element-198">
              <X className="h-5 w-5" data-id="element-199" />
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden" data-id="element-200">
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-id="element-201">
              {/* Site Field */}
              <div className="space-y-2" ref={dropdownRef} data-id="element-202">
                <label htmlFor="site" className="block text-sm font-medium text-[#062133]" data-id="element-203">
                  Site
                </label>

                {mode === 'site-prefilled' ? <input type="text" id="site" value={formData.site} disabled className="w-full px-4 py-3 bg-[#fafaf9] border border-[#e5e5e5] rounded-xl text-[#5a7280] cursor-not-allowed" data-id="element-204" /> : <div className="relative" data-id="element-205">
                    <div className="relative" data-id="element-206">
                      <input type="text" id="site" value={siteSearch || formData.site} onChange={e => {
                    setSiteSearch(e.target.value);
                    setIsSiteDropdownOpen(true);
                    setFormData(prev => ({
                      ...prev,
                      site: ''
                    })); // Clear site until selected
                  }} onFocus={() => setIsSiteDropdownOpen(true)} placeholder="Search for a site..." className="w-full px-4 py-3 bg-white border border-[#e5e5e5] rounded-xl text-[#062133] placeholder-[#5a7280]/60 focus:outline-none focus:ring-2 focus:ring-[#ff697f] focus:border-transparent transition-colors pr-10" autoComplete="off" required={mode === 'site-searchable'} disabled={isSubmitting} data-id="element-207" />
                      <Search className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5a7280] pointer-events-none" data-id="element-208" />
                    </div>

                    {/* Dropdown Results */}
                    {isSiteDropdownOpen && <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#e5e5e5] rounded-xl shadow-xl max-h-60 overflow-y-auto z-20" data-id="element-209">
                        {filteredSites.length > 0 ? filteredSites.map(site => <button key={site} type="button" onClick={() => handleSiteSelect(site)} className="w-full text-left px-4 py-3 text-sm text-[#062133] hover:bg-[#fafaf9] transition-colors flex items-center justify-between" data-id="element-210">
                              {site}
                              {formData.site === site && <Check className="h-4 w-4 text-[#ff697f]" data-id="element-211" />}
                            </button>) : <div className="px-4 py-3 text-sm text-[#5a7280] text-center" data-id="element-212">
                            No sites found
                          </div>}
                      </div>}
                  </div>}
              </div>

              {/* Action Type Dropdown */}
              <div className="space-y-2" data-id="element-213">
                <label htmlFor="actionType" className="block text-sm font-medium text-[#062133]" data-id="element-214">
                  Action Type
                </label>
                <div className="relative" data-id="element-215">
                  <select id="actionType" value={formData.actionType} onChange={e => handleChange('actionType', e.target.value)} className="w-full px-4 py-3 bg-white border border-[#e5e5e5] rounded-xl text-[#062133] appearance-none cursor-pointer hover:border-[#5a7280] focus:outline-none focus:ring-2 focus:ring-[#ff697f] focus:border-transparent transition-colors disabled:bg-[#fafaf9] disabled:cursor-not-allowed" required disabled={isSubmitting} data-id="element-216">
                    <option value="" disabled data-id="element-217">
                      Select action type...
                    </option>
                    {actionTypes.map(type => <option key={type} value={type} data-id="element-218">
                        {type}
                      </option>)}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5a7280] pointer-events-none" data-id="element-219" />
                </div>
              </div>

              {/* Assign To Dropdown */}
              <div className="space-y-2" data-id="element-220">
                <label htmlFor="assignTo" className="block text-sm font-medium text-[#062133]" data-id="element-221">
                  Assign To
                </label>
                <div className="relative" data-id="element-222">
                  <select id="assignTo" value={formData.assignTo} onChange={e => handleChange('assignTo', e.target.value)} className="w-full px-4 py-3 bg-white border border-[#e5e5e5] rounded-xl text-[#062133] appearance-none cursor-pointer hover:border-[#5a7280] focus:outline-none focus:ring-2 focus:ring-[#ff697f] focus:border-transparent transition-colors disabled:bg-[#fafaf9] disabled:cursor-not-allowed" required disabled={isSubmitting} data-id="element-223">
                    <option value="" disabled data-id="element-224">
                      Select assignee...
                    </option>
                    {assignees.map(assignee => <option key={assignee} value={assignee} data-id="element-225">
                        {assignee}
                      </option>)}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5a7280] pointer-events-none" data-id="element-226" />
                </div>
              </div>

              {/* Suggested Relievers Section - Only shown for Real-Time Exception */}
              {isRealTimeException && <div className="space-y-4 pt-4 border-t border-[#e5e5e5]" data-id="element-227">
                  {/* Section Header */}
                  <div className="flex items-center gap-2" data-id="element-228">
                    <h3 className="text-sm font-semibold text-[#062133]" data-id="element-229">
                      Suggested Relievers
                    </h3>
                    <div className="relative" data-id="element-230">
                      <button type="button" className="p-1 text-[#5a7280] hover:text-[#062133] transition-colors" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} onClick={() => setShowTooltip(!showTooltip)} aria-label="More information" data-id="element-231">
                        <Info className="h-4 w-4" data-id="element-232" />
                      </button>
                      {/* Tooltip */}
                      {showTooltip && <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-[#062133] text-white text-xs rounded-lg whitespace-nowrap z-10 shadow-lg" data-id="element-233">
                          Guards who can cover this shift without incurring
                          overtime
                          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#062133]" data-id="element-234" />
                        </div>}
                    </div>
                  </div>

                  {/* Guard Cards */}
                  <div className="space-y-3" data-id="element-235">
                    {suggestedGuards.map(guard => {
                  const isSelected = selectedGuardId === guard.id;
                  return <div key={guard.id} className={`p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-[#ff697f] bg-[#ff697f]/5' : 'border-[#e5e5e5] bg-white hover:border-[#5a7280]/30'}`} data-id="element-236">
                          <div className="flex items-start justify-between gap-3" data-id="element-237">
                            <div className="flex-1 min-w-0" data-id="element-238">
                              <h4 className="font-semibold text-[#062133] truncate" data-id="element-239">
                                {guard.name}
                              </h4>
                              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs" data-id="element-240">
                                <div className="flex items-center gap-1.5 text-[#5a7280]" data-id="element-241">
                                  <Calendar className="h-3.5 w-3.5 flex-shrink-0" data-id="element-242" />
                                  <span data-id="element-243">
                                    {guard.currentShifts} of {guard.maxShifts}{' '}
                                    shifts
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 text-[#5a7280]" data-id="element-244">
                                  <Shield className="h-3.5 w-3.5 flex-shrink-0" data-id="element-245" />
                                  <span data-id="element-246">{guard.grade}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-[#5a7280]" data-id="element-247">
                                  <MapPin className="h-3.5 w-3.5 flex-shrink-0" data-id="element-248" />
                                  <span data-id="element-249">{guard.distance}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-[#5a7280]" data-id="element-250">
                                  <Phone className="h-3.5 w-3.5 flex-shrink-0" data-id="element-251" />
                                  <span data-id="element-252">{guard.contact}</span>
                                </div>
                              </div>
                            </div>
                            <button type="button" onClick={() => handleSelectGuard(guard.id)} disabled={isSubmitting} className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${isSelected ? 'bg-[#ff697f] text-white cursor-default' : 'bg-white border border-[#e5e5e5] text-[#5a7280] hover:border-[#ff697f] hover:text-[#ff697f]'} disabled:opacity-50 disabled:cursor-not-allowed`} data-id="element-253">
                              {isSelected ? <span className="flex items-center gap-1" data-id="element-254">
                                  <Check className="h-3.5 w-3.5" data-id="element-255" />
                                  Selected
                                </span> : 'Select'}
                            </button>
                          </div>
                        </div>;
                })}
                  </div>

                  {/* Dispatch Button */}
                  <button type="button" onClick={handleDispatch} disabled={!selectedGuardId || isSubmitting} className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-xl transition-all ${selectedGuardId ? 'bg-[#062133] text-white hover:bg-[#0a3249] shadow-sm' : 'bg-[#e5e5e5] text-[#5a7280] cursor-not-allowed'} disabled:opacity-50 disabled:cursor-not-allowed`} data-id="element-256">
                    <Send className="h-4 w-4" data-id="element-257" />
                    Dispatch Selected
                  </button>
                </div>}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-5 border-t border-[#e5e5e5] bg-[#fafaf9]" data-id="element-258">
              <button type="button" onClick={onClose} disabled={isSubmitting} className="px-5 py-2.5 text-sm font-medium text-[#5a7280] bg-white border border-[#e5e5e5] rounded-xl hover:bg-[#fafaf9] hover:text-[#062133] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#062133] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-259">
                Cancel
              </button>
              <button type="submit" disabled={mode === 'site-searchable' && !formData.site || isSubmitting} className={`px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-colors shadow-sm flex items-center gap-2 ${mode === 'site-searchable' && !formData.site || isSubmitting ? 'bg-[#e5e5e5] cursor-not-allowed' : 'bg-[#ff697f] hover:bg-[#e65a6f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#ff697f]'}`} data-id="element-260">
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-261" />}
                Create Action
              </button>
            </div>
          </form>
        </div>
      </div>
    </>;
}